import {Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ConfiguracaoSeletor, OpcaoSelecao} from '../interfaces/opcao-selecao.interface';
import {ReplaySubject, Subject} from 'rxjs';

@Component({
    selector: 'app-combo-generico',
    templateUrl: './combo-generico.component.html',
    styleUrls: ['./combo-generico.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ComboGenericoComponent),
            multi: true
        }
    ]
})
export class ComboGenericoComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {

    @Input() opcoes: OpcaoSelecao[] = [];
    @Input() configuracao: ConfiguracaoSeletor = {};
    @Input() valor: any = null;
    @Input() desabilitado: boolean = false;
    @Input() placeholder: string;

    @Output() mudancaSelecao = new EventEmitter<any>();

    // Propriedades para pesquisa
    termoPesquisa: string = '';
    opcoesFiltradas: OpcaoSelecao[] = [];
    pesquisando: boolean = false;

    // Propriedades para ngx-mat-select-search
    opcoesFilter: ReplaySubject<OpcaoSelecao[]> = new ReplaySubject<OpcaoSelecao[]>(1);
    _onDestroy = new Subject<void>();
    // Configuração padrão
    configuracaoPadrao: ConfiguracaoSeletor = {
        exibirPlaceholder: true,
        placeholder: 'Selecione...',
        mostrarOpcaoVazia: true,
        textoOpcaoVazia: '-- Nenhuma --',
        desabilitado: false,
        obrigatorio: false,
        multiplo: false,
        campoExibicao: 'nome',
        campoValor: 'id',
        // Configurações de pesquisa
        habilitarPesquisa: false,
        placeholderPesquisa: 'Pesquisar...',
        camposPesquisa: ['nome'],
        sensivelMaiuscula: false,
        tamanhoMinimoPesquisa: 0
    };
    // Configuração mesclada
    configuracaoMesclada: ConfiguracaoSeletor = {};

    ngOnInit(): void {
        this.configuracaoMesclada = {...this.configuracaoPadrao, ...this.configuracao};
        if (this.placeholder) {
            this.configuracaoMesclada.placeholder = this.placeholder;
        }
        this.opcoesFiltradas = [...this.opcoes];
        this.initOpcoesFilter();
    }

    ngOnChanges(mudancas: SimpleChanges): void {
        if (mudancas['opcoes']) {
            this.opcoesFiltradas = [...this.opcoes];
            this.initOpcoesFilter();
        }
        if (mudancas['configuracao']) {
            this.configuracaoMesclada = {...this.configuracaoPadrao, ...this.configuracao};
        }
        if (mudancas['valor']) {
            this.valor = mudancas['valor'].currentValue;
        }
    }

    onMudancaSelecao(evento: any): void {
        const valorSelecionado = evento.value;
        this.valor = valorSelecionado;
        this.onChange(valorSelecionado);
        this.onTouched();
        this.mudancaSelecao.emit(valorSelecionado);
    }

    obterValorExibicao(opcao: OpcaoSelecao): string {
        const campo = this.configuracaoMesclada.campoExibicao || 'nome';
        return opcao[campo as keyof OpcaoSelecao] as string;
    }

    obterValor(opcao: OpcaoSelecao): any {
        const campo = this.configuracaoMesclada.campoValor || 'id';
        return opcao[campo as keyof OpcaoSelecao];
    }

    // Métodos de pesquisa
    onMudancaPesquisa(termoPesquisa: string): void {
        this.termoPesquisa = termoPesquisa;
        this.pesquisando = termoPesquisa && termoPesquisa.length > 0;
        this.aplicarPesquisa();
    }

    aplicarPesquisa(): void {
        if (!this.configuracaoMesclada.habilitarPesquisa) {
            this.opcoesFiltradas = [...this.opcoes];
            this.opcoesFilter.next([...this.opcoes]);
            return;
        }

        if (!this.termoPesquisa || this.termoPesquisa.trim() === '') {
            this.opcoesFiltradas = [...this.opcoes];
            this.opcoesFilter.next([...this.opcoes]);
            return;
        }

        const tamanhoMinimo = this.configuracaoMesclada.tamanhoMinimoPesquisa || 0;
        if (this.termoPesquisa.length < tamanhoMinimo) {
            this.opcoesFiltradas = [...this.opcoes];
            this.opcoesFilter.next([...this.opcoes]);
            return;
        }

        const termoPesquisa = this.configuracaoMesclada.sensivelMaiuscula
            ? this.termoPesquisa
            : this.termoPesquisa.toLowerCase();

        this.opcoesFiltradas = this.opcoes.filter(opcao => {
            const camposPesquisa = this.configuracaoMesclada.camposPesquisa || ['nome'];

            return camposPesquisa.some(campo => {
                const valorCampo = opcao[campo as keyof OpcaoSelecao] as string;
                if (!valorCampo) return false;

                const valor = this.configuracaoMesclada.sensivelMaiuscula
                    ? valorCampo
                    : valorCampo.toLowerCase();

                return valor.includes(termoPesquisa);
            });
        });

        this.opcoesFilter.next([...this.opcoesFiltradas]);
    }

    limparPesquisa(): void {
        this.termoPesquisa = '';
        this.pesquisando = false;
        this.opcoesFiltradas = [...this.opcoes];
        this.opcoesFilter.next([...this.opcoes]);
    }

    // Métodos para ngx-mat-select-search
    initOpcoesFilter(): void {
        if (this.opcoes) {
            this.opcoesFilter.next([...this.opcoes]);
        }
    }

    ngOnDestroy(): void {
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    // ControlValueAccessor methods
    writeValue(valor: any): void {
        this.valor = valor;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(desabilitado: boolean): void {
        this.desabilitado = desabilitado;
    }

    // ControlValueAccessor implementation
    private onChange = (valor: any) => {
    };

    private onTouched = () => {
    };
}
